import { Component, EventEmitter, Input, Output, inject,
    ChangeDetectionStrategy
} from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { EnaioDocument } from 'src/modules/enaio/shared/EnaioDocument';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { BackendResponse } from 'src/modules/sm-base/shared/backend-response.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';

@Component({
    selector: 'enaio-object',
    templateUrl: './enaio-object.component.html',
    styleUrls: ["./enaio-object.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EnaioObjectComponent extends ComponentView {

    app: MainAppService = inject(MainAppService);

    @Input()
    showMetaData = true;
    @Input()
    showDocument = true;

    @Output()
    onTokenExpired = new EventEmitter<string>();
    @Output()
    onDocumentLoaded = new EventEmitter<string>();

    _documentToken: string = null;
    _remarkObjects: FrontendFieldListItem[] = [];

    document: EnaioDocument;
    table: TableData;

    selectedRemarkObjectToken: string = null;
    loadedToken: string = null;
    loadingToken: string = null;
    loading = false;
    loadError: string = null;
    pdfSrc: Blob | string;

    @Input()
    set documentToken(value: string) {
        if (this._documentToken != value) {
            this._documentToken = value;
            void this.updateData();
        }
    }

    @Input()
    set remarkObjects(value: FrontendFieldListItem[]) {
        this._remarkObjects = value ?? [];
        this.selectedRemarkObjectToken = Utils.isNoe(value) ? null : value[0].value;
        void this.updateData();
    }

    async updateData(force = false): Promise<void> {
        const token = this.getCurrentToken();
        if (!force && (token == this.loadedToken || token == this.loadingToken)) {
            return;
        }
        if (Utils.isNoe(token)) {
            this.loadedToken = null;
            this.loadingToken = null;
            this.loading = false;
            this.loadError = null;
            this.document = null;
            this.pdfSrc = "";
            this.updateMetaDataTable();
            return;
        }

        this.loadingToken = token;
        this.loading = true;
        this.loadError = null;
        this.document = null;
        this.pdfSrc = "";
        this.updateMetaDataTable();
        try {
            const document = await RestEndpoint.main().query({token, document: this.showDocument, pdf: true}).run("api/doc/document/object").get(EnaioDocument);
            if (token != this.getCurrentToken()) {
                return;
            }

            this.document = document;
            this.pdfSrc = this.showDocument ? GuiUtils.base64ToBlob(document.documentBase64) : null;
            this.loadedToken = token;
            if (!this.showDocument) {
                this.onDocumentLoaded.emit(token);
            }
        }
        catch (error) {
            if (token != this.getCurrentToken()) {
                return;
            }

            this.loadedToken = null;
            this.pdfSrc = "";
            const response = BackendResponse.getFromException(error);
            this.loadError = this.app.t("documents.previewLoadFailed");
            if (response?.isErrorId("tokenExpired")) {
                this.onTokenExpired.emit(token);
            }
        }
        finally {
            if (this.loadingToken == token) {
                this.loadingToken = null;
                this.loading = false;
                this.updateMetaDataTable();
            }
        }
    }

    async retryLoad(): Promise<void> {
        this.loadedToken = null;
        await this.updateData(true);
    }

    previewLoaded(): void {
        if (!Utils.isNoe(this.loadedToken) && this.loadedToken == this.getCurrentToken()) {
            this.onDocumentLoaded.emit(this.loadedToken);
        }
    }

    previewLoadFailed(): void {
        if (this.loadedToken == this.getCurrentToken()) {
            this.loadedToken = null;
            this.pdfSrc = "";
            this.loadError = this.app.t("documents.previewLoadFailed");
        }
    }

    private getCurrentToken(): string {
        return this.selectedRemarkObjectToken ?? this._documentToken;
    }

    private updateMetaDataTable(): void {
        this.table = new TableData([
            new TableColumn("field", this.app.t("general.property"), TableCellType.string, 30),
            new TableColumn("value", this.app.t("general.value"), TableCellType.string, -1)
        ], (this.document?.metaData ?? []).filter(field => !field.field.system)
                .map(md => new TableRow("", md, {
                field: this.app.t("objectDef." + this.document.objectType.name + "." + md.field.name, null, md.field.getBestDisplayName()),
                value: new TableCell(Strings.replaceAll(md.getDisplayValue(), "\\\n", "<br>"), { keepHtml: true })
            })));
    }

}
